import * as Actions from '../actions/activityTypeActions';
import ActivityType from '../../entities/activityType';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ActivityTypeAction = { 
    type: string; 
    activityType: ActivityType | null; 
    activityTypeId: string | ActivityType; 
    partial: boolean; 
    lastFetch: number; 
    activityTypes: ActivityType[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<ActivityType> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {ActivityType[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<ActivityType> | null, firmId: string, userId: string, updates?: ActivityType[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'activityType', 'activityTypes', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<ActivityType>} data
 * @param {ActivityType[]} [updates]
 * @returns {ArrayMap<ActivityType>}
 */
function updateStorage(data: ArrayMap<ActivityType>, updates?: ActivityType[]): ArrayMap<ActivityType> {
	if (logging) console.debug('⮂ activityTypeReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {ActivityType[]} [updates]
 * @returns {ArrayMap<ActivityType>}
 */
function updateStorageNoReturn(updates?: ActivityType[]): void {
	if (logging) console.debug('⮂ activityTypeReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {ActivityType[]} [data]
 * @returns {ArrayMap<ActivityType>}
 */
function createState(data?: ActivityType[] | ArrayMap<ActivityType>): ArrayMap<ActivityType> {
	if (!data) return ArrayMapRegistry.clone<ActivityType>('activityType', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<ActivityType>('activityType', data.map(e => e));
    else return ArrayMapRegistry.clone<ActivityType>('activityType', data);
}

function activityTypes(state: ArrayMap<ActivityType>, action: ActivityTypeAction): ArrayMap<ActivityType> {
	if (!state) state = createState();

	let st: ArrayMap<ActivityType>;

	switch (action.type) {
		// action.activityType - the new value of activityType with id action.id
		// action.activityTypeId - id of the target, used if new value is null
		case Actions.ACTIVITYTYPE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ activityTypeReducer: FETCH_ONE_SUCCESS', action);

			if (action.activityType === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.activityTypeId)) {
				st = state.clone();
				st.set(action.activityType);
				// state.clear();

				updateStorageNoReturn([action.activityType]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.activityTypes
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.activityTypes
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.activityTypes
			 !partial, !stateEmpty, fetchEmpty	-> D return activityTypes in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge activityTypes in state newer than those in action.activityTypes
		 */
		case Actions.ACTIVITYTYPE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.activityTypes.length === 0;
			const fetched = action.activityTypes.map(activityType => new ActivityType({...activityType, persisted: true}));

			if (logging) console.debug(`⮂ activityTypeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} activityTypes with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ activityTypeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ activityTypeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<ActivityType> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: ActivityType[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(activityType => {
					const current = st.get(activityType.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && activityType.status === 'D') {
							// Remove it from state
							toUpdate.push(activityType);
							st.delete(activityType);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (activityType.addedOn > current.addedOn) {
								toUpdate.push(activityType);
								st.set(activityType);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (activityType.status !== 'D') {
							toUpdate.push(activityType);
							st.set(activityType);
						}
						else if (logging) console.debug('⮂ activityTypeReducer: partialMerge: skipping deleted activityType not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ activityTypeReducer: empty: activityTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ activityTypeReducer: fullMerge: activityTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localActivityType => {
                    // If this activityType has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localActivityType.persisted) {
                        const fetchedActivityType = st.get(localActivityType.id);
                        if (!fetchedActivityType || localActivityType.addedOn > fetchedActivityType.addedOn) st.set(localActivityType);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.ACTIVITYTYPE_FETCH_ONE_FAILURE:
			console.error('ACTIVITYTYPE_FETCH_ONE_FAILURE', action.activityTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.ACTIVITYTYPE_FETCH_FAILURE:
			console.error('ACTIVITYTYPE_FETCH_FAILURE', action.activityTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.ACTIVITYTYPE_RESTORE: {
		    let activityType = new ActivityType({...action.activityType});
            st = state.clone();

            const existing = st.get(activityType.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (activityType.addedOn >= existing.addedOn) {
                    st.set(activityType);
                    return updateStorage(st, [activityType]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (activityType.status === 'D') return state;

                st.set(activityType);
                return updateStorage(st, [activityType]);
            }
        }
            
		case Actions.ACTIVITYTYPE_SAVE: {
		    // console.debug('⮂ ACTIVITYTYPE_SAVE reducer', action.activityType?.id);
		    
			// clone the activityType
			let activityType = new ActivityType({...action.activityType});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new activityType in the state
			// If this is a create, add to the queue
			const _activityType = st.get(activityType.id);

			if (!_activityType) st.set(activityType);
			else {
				if (activityType.addedOn >= _activityType.addedOn) st.set(activityType);
			}

			return updateStorage(st, [activityType]);
		}

		case Actions.ACTIVITYTYPE_SAVE_MANY: {
		    if (logging) console.debug('⮂ ACTIVITYTYPE_SAVE_MANY reducer', action.activityTypes.map(activityType => activityType.id).join(','));
		    
			// clone the activityTypes
			let toProcess = action.activityTypes.map(activityType => new ActivityType(activityType));

			st = state.clone();
			// state.clear();

			const updates: ActivityType[] = [];

			toProcess.forEach(activityType => {
				// Add or update the new activityType in the state
				// If this is a create, add to the queue
				const existing = st.get(activityType.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (activityType.status !== 'D') {
						st.push(activityType);
						updates.push(activityType);
					}
					else if (logging) console.debug('⮂ ACTIVITYTYPE_SAVE_MANY: skipping deleted activityType not in state');
				}
				else {
					if (activityType.addedOn >= existing.addedOn) {
						st.set(activityType);
						updates.push(activityType);
					}
					else if (logging) console.debug('⮂ Skipping, existing activityType had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACTIVITYTYPE_REMOVE: {
			// clone the activityType
			const activityType = new ActivityType({...action.activityType});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(activityType);

			return updateStorage(st, [activityType]);
		}

		case Actions.ACTIVITYTYPE_RESTORE_FAILURE:
		case Actions.ACTIVITYTYPE_SAVE_FAILURE: {
		    console.warn('ACTIVITYTYPE_SAVE_FAILURE reducer', action.activityType?.id);
		    if (!action.activityType?.id) {
				console.warn('ACTIVITYTYPE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const activityType = ensure(ActivityType, action.activityType);
			if (!activityType) {
				console.error('Could not find activityType after call to save');
				return state;
			}

			st = state.clone();

			const _activityTypeRef = state.get(activityType.id);

			if (_activityTypeRef && (_activityTypeRef.addedOn === activityType.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _activityType = new ActivityType({..._activityTypeRef});
				_activityType.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_activityType.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('activityType', _activityType).then(r => {
						if (r.isErr()) console.error(`⮂ activityTypeReducer: failed to save activityType with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ activityTypeReducer: saved activityType with fatalError info');
					}).catch(err => console.error(`⮂ activityTypeReducer: failed to save activityType with fatalError info: ${err.message}`));

				st.set(_activityType);
			}

			return updateStorage(st);
		}

		case Actions.ACTIVITYTYPE_SAVE_MANY_FAILURE: {
		    console.warn('ACTIVITYTYPE_SAVE_MANY_FAILURE reducer', action.activityTypes.map(activityType => activityType.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.ACTIVITYTYPE_RESTORE_SUCCESS:
		case Actions.ACTIVITYTYPE_SAVE_SUCCESS: {
			if (!action.activityType?.id) {
				console.warn('ACTIVITYTYPE_SAVE_SUCCESS reducer');
                return state;
			}

			const activityTypeRef = state.get(action.activityType?.id);
			if (!activityTypeRef) {
				console.warn('Could not find activityType after call to save');
                return state;
			}

			st = state.clone();

			if (action.activityType.addedOn >= activityTypeRef.addedOn) {
				st.set(action.activityType);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const activityType = new ActivityType({...activityTypeRef, persisted: true});
				st.set(activityType);
			}

			return updateStorage(st, [action.activityType]);
		}

		case Actions.ACTIVITYTYPE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ ACTIVITYTYPE_SAVE_MANY_SUCCESS reducer', action.activityTypes.map(activityType => activityType.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const activityTypes: ActivityType[] = [];

			action.activityTypes.forEach(e => {
				const activityTypeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!activityTypeRef) console.warn('Could not find activityType after call to save');
				else if (e.addedOn >= activityTypeRef.addedOn) {
				    st.set(e);
				    activityTypes.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const activityType = new ActivityType({...activityTypeRef, persisted: true});
					st.set(activityType);
					activityTypes.push(activityType);
				}
			});

			return updateStorage(st, activityTypes);
		}

		case Actions.ACTIVITYTYPE_REMOVE_FAILURE: {
		    console.warn('ACTIVITYTYPE_REMOVE_FAILURE action', action.activityType?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.ACTIVITYTYPE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.ACTIVITYTYPE_RESTORE_MANY: {
			if (logging) console.debug('⮂ ACTIVITYTYPE_RESTORE_MANY reducer', action.activityTypes.map(activityType => activityType.id).join(','));

			const toProcess = action.activityTypes.map(activityType => new ActivityType({...activityType}));
			st = state.clone();

			const updates: ActivityType[] = [];

			toProcess.forEach(activityType => {
				const existing = st.get(activityType.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (activityType.addedOn >= existing.addedOn) {
						st.set(activityType);
						updates.push(activityType);
					}
					else if (logging) console.debug('⮂ ACTIVITYTYPE_RESTORE_MANY: skipping older version of activityType');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (activityType.status !== 'D') {
						st.set(activityType);
						updates.push(activityType);
					}
					else if (logging) console.debug('⮂ ACTIVITYTYPE_RESTORE_MANY: skipping deleted activityType not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.ACTIVITYTYPE_REMOVE_MANY: {
			if (logging) console.debug('⮂ ACTIVITYTYPE_REMOVE_MANY reducer', action.activityTypes.map(activityType => activityType.id).join(','));

			const toProcess = action.activityTypes.map(activityType => new ActivityType({...activityType}));
			st = state.clone();

			toProcess.forEach(activityType => {
				st.delete(activityType);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default activityTypes;